<template>
  <section class="tx">
    <div class="tx-bar">
      <input
        v-model="query"
        class="tx-search"
        type="search"
        :placeholder="placeholder"
        aria-label="Find in the transcript"
      />
      <span class="tx-hint">Tap a line to play from there</span>
      <button
        class="tx-follow"
        :class="{ on: follow }"
        type="button"
        :aria-pressed="follow ? 'true' : 'false'"
        @click="follow = !follow"
      >
        <span class="tx-toggle"></span>Follow playback
      </button>
    </div>

    <div ref="box" class="tx-scroll">
      <p v-if="!lines.length" class="tempty">
        No transcript was captured for this recording.
      </p>
      <div
        v-for="(l, i) in lines"
        v-show="visible(l, i)"
        :key="i"
        ref="lineEls"
        class="tx-line"
        :class="{ active: i === activeIdx, pulse: i === pulseIdx }"
        @click="$emit('seek', l.sec)"
      >
        <span
          v-if="findingLineSet.has(i)"
          class="tx-gutter"
          aria-hidden="true"
        ></span>
        <button
          class="tx-ts"
          type="button"
          :aria-label="`Jump to ${l.t}`"
          @click.stop="$emit('seek', l.sec)"
        >
          {{ l.t }}
        </button>
        <span class="tx-text"
          ><template v-for="(seg, si) in segments(l.text)"
            ><span v-if="seg.hit" :key="si" class="hit">{{ seg.text }}</span
            ><template v-else>{{ seg.text }}</template></template
          ></span
        >
      </div>
      <p v-if="query && !anyMatch" class="tempty">
        No lines match “{{ query }}”.
      </p>
    </div>
    <button
      v-if="lines.length > window * 2 + 1 && !q"
      class="tx-more"
      type="button"
      @click="compact = !compact"
    >
      {{ compact ? 'Show the full transcript' : 'Show less'
      }}<span class="b">{{ lines.length }} lines</span>
    </button>
  </section>
</template>

<script>
export default {
  name: 'EvtTranscript',
  props: {
    lines: { type: Array, default: () => [] },
    currentTime: { type: Number, default: 0 },
    // seconds of each analysis finding - the nearest line gets a gutter marker
    findingSecs: { type: Array, default: () => [] },
  },
  data() {
    // compact: only the lines around the playhead, so the page reads as a player with
    // context rather than a wall of text; search or 'Show the full transcript' opens it
    return {
      query: '',
      activeIdx: -1,
      follow: true,
      holdUntil: 0,
      pulseIdx: -1,
      compact: true,
      window: 3,
    };
  },
  computed: {
    placeholder() {
      return this.lines.length
        ? `Find in ${this.lines.length} lines…`
        : 'Find in the transcript…';
    },
    q() {
      return this.query.trim().toLowerCase();
    },
    countLabel() {
      return `${this.lines.length} lines`;
    },
    anyMatch() {
      if (!this.q) return true;
      return this.lines.some(
        (l) => (l.text || '').toLowerCase().indexOf(this.q) > -1
      );
    },
    findingLineSet() {
      const set = new Set();
      (this.findingSecs || []).forEach((sec) => {
        const idx = this.nearestIdx(sec);
        if (idx > -1) set.add(idx);
      });
      return set;
    },
  },
  watch: {
    currentTime(ct) {
      const idx = this.activeFor(ct);
      if (idx === this.activeIdx) return;
      this.activeIdx = idx;
      if (this.follow && idx > -1 && Date.now() > this.holdUntil)
        this.scrollIdxIntoView(idx);
    },
  },
  methods: {
    matches(l) {
      return !this.q || (l.text || '').toLowerCase().indexOf(this.q) > -1;
    },
    visible(l, i) {
      if (!this.matches(l)) return false;
      if (!this.compact || this.q) return true;
      const c = this.activeIdx > -1 ? this.activeIdx : 0;
      return i >= c - this.window && i <= c + this.window;
    },
    // split a line into {text, hit} segments around the search query (safe, no v-html)
    segments(text) {
      const q = this.q;
      if (!q) return [{ text, hit: false }];
      const out = [];
      const low = (text || '').toLowerCase();
      let i = 0;
      let at = low.indexOf(q, i);
      while (at > -1) {
        if (at > i) out.push({ text: text.slice(i, at), hit: false });
        out.push({ text: text.slice(at, at + q.length), hit: true });
        i = at + q.length;
        at = low.indexOf(q, i);
      }
      if (i < text.length) out.push({ text: text.slice(i), hit: false });
      return out;
    },
    // last line whose sec <= ct (binary search)
    activeFor(ct) {
      const a = this.lines;
      let lo = 0;
      let hi = a.length - 1;
      let ans = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (a[mid].sec <= ct) {
          ans = mid;
          lo = mid + 1;
        } else hi = mid - 1;
      }
      return ans;
    },
    // nearest line index to a target second (binary search + neighbour check)
    nearestIdx(sec) {
      const a = this.lines;
      if (!a.length) return -1;
      let lo = 0;
      let hi = a.length - 1;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (a[mid].sec < sec) lo = mid + 1;
        else hi = mid;
      }
      if (lo > 0 && Math.abs(a[lo - 1].sec - sec) <= Math.abs(a[lo].sec - sec))
        return lo - 1;
      return lo;
    },
    // pause follow-playback when the USER scrolls (wheel/touch), not on programmatic scroll
    onUserScroll() {
      this.holdUntil = Date.now() + 2600;
    },
    scrollIdxIntoView(idx) {
      this.$nextTick(() => {
        const el = this.$refs.lineEls && this.$refs.lineEls[idx];
        if (el && el.scrollIntoView)
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    },
    // called by parent on any seek → scroll nearest line to center + pulse it
    scrollToSec(sec) {
      const idx = this.nearestIdx(sec);
      if (idx < 0) return;
      this.holdUntil = 0; // allow this programmatic scroll
      this.activeIdx = idx;
      this.scrollIdxIntoView(idx);
      this.pulseIdx = idx;
      clearTimeout(this._pulseT);
      this._pulseT = setTimeout(() => {
        this.pulseIdx = -1;
      }, 1000);
    },
  },
  mounted() {
    this._us = () => this.onUserScroll();
    window.addEventListener('wheel', this._us, { passive: true });
    window.addEventListener('touchmove', this._us, { passive: true });
  },
  beforeDestroy() {
    clearTimeout(this._pulseT);
    if (this._us) {
      window.removeEventListener('wheel', this._us);
      window.removeEventListener('touchmove', this._us);
    }
  },
};
</script>
